import { Link } from 'react-router-dom';
import { useSeo } from '@/hooks/useSeo';
import { siteInfo } from '@/mocks/site';
import PageHeader from '@/components/feature/PageHeader';
import SubscribeForm from '@/components/feature/SubscribeForm';

const principles = [
  {
    icon: 'ri-eye-line',
    title: '只做信息聚合',
    text: '我们汇总已经公开传播的信息，标注来源时间与讨论热度，不对事件真伪下结论，也不替当事人做任何判断。',
  },
  {
    icon: 'ri-shield-check-line',
    title: '不制造对立',
    text: '涉及具体人物时，我们只描述公开行为，不引导谩骂、不放大对立，评论区也会对人身攻击类内容做处理。',
  },
  {
    icon: 'ri-scales-3-line',
    title: '标注不确定性',
    text: '尚未被回应的说法统一打上「传闻」「待核实」标签。热度高不等于事实真，这个边界我们一直守着。',
  },
  {
    icon: 'ri-refresh-line',
    title: '及时纠错',
    text: '如果后续出现反转或澄清，我们会在原速报中补充更新说明，而不是悄悄删掉重发。',
  },
];

const voices = [
  {
    name: '阿柚',
    role: '通勤党',
    text: '以前刷热点要开好几个 App，现在早上看一眼瓜报就知道今天该关注什么，省下来的时间够我多睡十分钟。',
  },
  {
    name: '小林同学',
    role: '追剧爱好者',
    text: '最喜欢它的口碑曲线，一部剧从开分到翻身的过程被讲得很清楚，比单看一个评分有用多了。',
  },
  {
    name: '夜班编辑 K',
    role: '内容从业者',
    text: '时间线整理得很克制，不会为了戏剧性去脑补细节，这点在同类站点里挺难得的。',
  },
];

const method = [
  { label: '公开讨论量', weight: 35 },
  { label: '搜索热度', weight: 25 },
  { label: '传播速度', weight: 25 },
  { label: '互动密度', weight: 15 },
];

export default function About() {
  useSeo({
    title: '关于我们 - 瓜圈速报内容原则与团队介绍',
    description:
      '了解瓜圈速报的定位、内容原则与热度计算方法。我们聚合公开传播的娱乐热点，标注不确定性，不制造对立，也不对个人私生活做判断。',
    keywords: '瓜圈速报,关于瓜圈速报,内容原则,热度算法,娱乐资讯站',
    path: '/about',
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'AboutPage',
        name: '关于瓜圈速报',
        description: '瓜圈速报的品牌定位、内容原则与热度计算方法说明。',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: siteInfo.name,
        description: siteInfo.description,
        email: siteInfo.email,
      },
      {
        '@context': 'https://schema.org',
        '@type': 'ContactPage',
        name: '联系瓜圈速报',
        description: '线索投稿与内容纠错的联系渠道。',
      },
    ],
  });

  return (
    <>
      <PageHeader
        crumbs={[{ label: '首页', to: '/' }, { label: '关于我们' }]}
        eyebrow="ABOUT US"
        title="我们只是把瓜，排好队"
        description="瓜圈速报不是媒体，也不是舆论裁判。我们做的事情很朴素：把散落在各处的公开信息收拢起来，按热度排序、按话题归类，让你用最少的时间知道今天发生了什么。"
      />

      <section className="mx-auto w-full max-w-[1280px] px-4 py-12 md:px-6 md:py-16">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-12">
          <div className="lg:col-span-7">
            <h2 className="font-heading text-2xl tracking-wide text-foreground-950 md:text-[30px]">网站定位</h2>
            <div className="mt-5 space-y-4 text-[15px] leading-[1.9] text-foreground-700">
              <p>
                {siteInfo.name}
                诞生于一个很具体的困扰：热点太多、平台太散，想搞清楚今天到底在传什么，往往要刷遍好几个信息流。于是我们试着做一个「收口」——把热度、话题和时间线放在同一个页面上。
              </p>
              <p>
                这里没有长篇深度报道，也没有情绪化的标题。我们提供的是一份可快速浏览的清单：哪几条最热，属于什么话题，后续有没有反转。看完之后要不要深入了解，交给你自己决定。
              </p>
              <p>
                我们相信一个简单的立场：娱乐资讯可以是轻松的，但不应该是伤害人的。所以对涉及具体个人的内容，我们始终保留一层克制。
              </p>
            </div>

            <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-4">
              {siteInfo.stats.map((stat) => (
                <div key={stat.label} className="rounded-card border border-background-200 bg-background-50 p-4">
                  <p className="font-heading text-2xl tabular-nums text-primary-600">
                    {stat.value}
                    <span className="ml-1 text-sm text-foreground-500">{stat.unit}</span>
                  </p>
                  <p className="mt-1.5 text-xs leading-relaxed text-foreground-600">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>

          <aside className="lg:col-span-5">
            <div className="rounded-card border border-background-200 bg-background-100 p-6 md:p-7">
              <h2 className="flex items-center gap-2 font-heading text-xl tracking-wide text-foreground-950">
                <i className="ri-bar-chart-2-line text-primary-500" />
                热度是怎么算出来的
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                四项指标加权汇总得到 0–10000 的相对热度分，数值只反映声量大小。
              </p>
              <ul className="mt-6 space-y-4">
                {method.map((row) => (
                  <li key={row.label}>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-foreground-700">{row.label}</span>
                      <span className="font-medium tabular-nums text-foreground-950">{row.weight}%</span>
                    </div>
                    <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-background-200">
                      <div className="h-full rounded-full bg-primary-500" style={{ width: `${row.weight * 2.4}%` }} />
                    </div>
                  </li>
                ))}
              </ul>
              <p className="mt-6 border-t border-background-200 pt-4 text-xs leading-relaxed text-foreground-500">
                榜单每 10 分钟刷新一次，历史维度会在今日、本周、本月三个切面留档。
              </p>
            </div>
          </aside>
        </div>
      </section>

      <section className="border-y border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-[1280px] px-4 py-12 md:px-6 md:py-16">
          <span className="inline-flex items-center gap-2 text-xs font-semibold tracking-[0.2em] text-primary-600">
            <span className="h-px w-6 bg-primary-400" />
            EDITORIAL PRINCIPLES
          </span>
          <h2 className="mt-2.5 font-heading text-2xl tracking-wide text-foreground-950 md:text-[32px]">
            内容原则，我们只守这四条
          </h2>
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 md:gap-5">
            {principles.map((item) => (
              <div
                key={item.title}
                className="rounded-card border border-background-200 bg-background-50 p-5 md:p-6"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-secondary-100 text-xl text-secondary-800">
                  <i className={item.icon} />
                </span>
                <h3 className="mt-4 text-base font-semibold text-foreground-950">{item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto w-full max-w-[1280px] px-4 py-12 md:px-6 md:py-16">
        <span className="inline-flex items-center gap-2 text-xs font-semibold tracking-[0.2em] text-primary-600">
          <span className="h-px w-6 bg-primary-400" />
          READERS' VOICE
        </span>
        <h2 className="mt-2.5 font-heading text-2xl tracking-wide text-foreground-950 md:text-[32px]">
          读者怎么说
        </h2>
        <div className="mt-8 grid grid-cols-1 gap-5 lg:grid-cols-3">
          {voices.map((voice) => (
            <figure
              key={voice.name}
              className="flex flex-col rounded-card border border-background-200 bg-background-50 p-6"
            >
              <span className="font-heading text-3xl leading-none text-accent-500">“</span>
              <blockquote className="mt-3 flex-1 text-sm leading-relaxed text-foreground-700">
                {voice.text}
              </blockquote>
              <figcaption className="mt-5 flex items-center gap-3 border-t border-background-200 pt-4">
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-100 text-sm font-semibold text-primary-700">
                  {voice.name.slice(0, 1)}
                </span>
                <span>
                  <span className="block text-sm font-medium text-foreground-900">{voice.name}</span>
                  <span className="block text-xs text-foreground-500">{voice.role}</span>
                </span>
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      <section className="mx-auto w-full max-w-[1280px] px-4 pb-16 md:px-6 md:pb-20">
        <div className="overflow-hidden rounded-card bg-primary-950 px-6 py-10 md:px-12 md:py-14">
          <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-12">
            <div className="lg:col-span-6">
              <span className="inline-flex items-center gap-2 rounded-full bg-accent-500 px-3 py-1 text-xs font-semibold text-primary-950">
                <i className="ri-mail-open-line" />
                联系我们
              </span>
              <h2 className="mt-4 font-heading text-2xl tracking-wide text-background-50 md:text-[34px]">
                有线索，或者想指出我们的错误
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-background-300">
                线索投稿、内容纠错、话题建议都欢迎。留下邮箱并选择关注方向，我们会尽快回复；紧急纠错也可以直接写信。
              </p>
              <a
                href={`mailto:${siteInfo.email}`}
                rel="nofollow"
                className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-accent-400 transition hover:text-accent-300"
              >
                <i className="ri-mail-line" />
                {siteInfo.email}
              </a>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link
                  to="/ranking"
                  className="inline-flex items-center gap-2 rounded-md border border-primary-800 px-4 py-2.5 text-sm font-medium text-background-200 transition hover:border-accent-500 hover:text-accent-400"
                >
                  查看热度排行
                </Link>
                <Link
                  to="/topics"
                  className="inline-flex items-center gap-2 rounded-md border border-primary-800 px-4 py-2.5 text-sm font-medium text-background-200 transition hover:border-accent-500 hover:text-accent-400"
                >
                  浏览话题榜
                </Link>
              </div>
            </div>
            <div className="lg:col-span-6">
              <SubscribeForm />
            </div>
          </div>
        </div>
      </section>
    </>
  );
}